<script setup>
import { useApi } from '@/utils/api'
import { formatRupiah } from '@/utils/formatRupiah'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()

// ─── Plans (dari GET /api/public/plans, bukan hardcode) ───────────────────────
const plans = ref([])
const plansLoading = ref(true)
const plansError = ref('')

// 2026-08-07: bulanan dihapus (keputusan produk: mengurangi frekuensi
// penagihan manual) -- sekarang 3 bulan / 6 bulan / tahunan.
const BILLING_PERIODS = ['quarterly', 'semi_annual', 'yearly']
const BILLING_PERIOD_LABELS = { quarterly: '3 Bulan', semi_annual: '6 Bulan', yearly: 'Tahunan' }
const BILLING_PERIOD_FREQUENCY = { quarterly: '3 bulan sekali', semi_annual: '6 bulan sekali', yearly: 'setahun sekali' }

const billingPeriod = ref(BILLING_PERIODS.includes(route.query.period) ? route.query.period : 'quarterly')

// Harga PER BULAN untuk periode tertentu. price_semi_annual_per_month
// datang langsung dari API (PlanResource) supaya diskonnya tidak perlu
// di-hardcode di frontend -- quarterly = price_monthly (0% diskon).
const perMonthPrice = (plan, period) => {
  if (!plan) return null

  return {
    quarterly: plan.price_monthly,
    semi_annual: plan.price_semi_annual_per_month,
    yearly: plan.price_yearly_per_month,
  }[period]
}

const fetchPlans = async () => {
  plansLoading.value = true
  plansError.value = ''
  try {
    const response = await useApi('/api/public/plans').get()
    const body = response?.data ?? response

    plans.value = Array.isArray(body) ? body : (body?.data ?? [])

    // Pre-select paket dari query (?plan=pro), jatuh ke paket pertama
    const preselect = plans.value.find(p => p.slug === route.query.plan)

    form.value.plan_id = (preselect ?? plans.value[0])?.id ?? null
  } catch (err) {
    plansError.value = 'Gagal memuat daftar paket. Silakan muat ulang halaman.'
    console.error('[Daftar] fetchPlans error', err)
  } finally {
    plansLoading.value = false
  }
}

onMounted(fetchPlans)

// ─── Form ─────────────────────────────────────────────────────────────────────
const form = ref({
  business_name: '',
  owner_name: '',
  email: '',
  phone: '',
  plan_id: null,
})

const loading = ref(false)
const errors = ref({})
const generalError = ref('')

// Estimasi harga di layar HANYA untuk ditampilkan ke calon pelanggan —
// nominal invoice sesungguhnya SELALU dihitung ulang di backend dari
// plans.price_monthly/price_yearly_per_month, bukan dari angka ini.
const selectedPlan = computed(() => plans.value.find(p => p.id === form.value.plan_id) ?? null)

const displayedPrice = computed(() => perMonthPrice(selectedPlan.value, billingPeriod.value))

const selectPlan = planId => {
  form.value.plan_id = planId
}

// ─── Submit ───────────────────────────────────────────────────────────────────
// Tombol di-disable begitu loading = true (idempotency sisi klien — cegah
// double-click bikin 2 submit). Pengecekan otoritatif tetap di backend
// (RegistrationService, dalam Cache::lock per-email).
//
// Addendum 13, Modul BE. Cabang Xendit/transfer bank ditentukan backend
// (platform_settings), bukan di sini -- frontend cuma membaca payment_method
// dari response POST registrations lalu menampilkan langkah yang sesuai:
//  - 'xendit'       -> redirect ke invoice_url (TIDAK berubah dari sebelumnya).
//  - 'bank_transfer' -> pindah ke step 'transfer' (halaman ini sendiri),
//    tampilkan instruksi transfer + form upload bukti.
const step = ref('form') // 'form' | 'transfer'
const transferInfo = ref(null)

const submit = async () => {
  if (loading.value) return

  loading.value = true
  errors.value = {}
  generalError.value = ''

  try {
    const response = await useApi('/api/public/registrations').post({
      body: {
        business_name: form.value.business_name,
        owner_name: form.value.owner_name,
        email: form.value.email,
        phone: form.value.phone,
        plan_id: form.value.plan_id,
        billing_period: billingPeriod.value,
      },
    })

    const body = response?.data ?? response
    const data = body?.data ?? body

    if (data?.payment_method === 'bank_transfer') {
      transferInfo.value = {
        registrationCode: data.registration_code,
        bankName: data.bank_name,
        bankAccountNumber: data.bank_account_number,
        bankAccountHolder: data.bank_account_holder,
        uniqueCode: data.unique_code,
        amount: data.amount,
        totalTransfer: data.total_transfer,
      }
      step.value = 'transfer'
      loading.value = false

      return
    }

    const invoiceUrl = data?.invoice_url

    if (invoiceUrl) {
      // Redirect penuh ke domain Xendit — bukan router.push (beda origin).
      window.location.href = invoiceUrl
    } else {
      generalError.value = 'Pendaftaran tersimpan tapi link pembayaran tidak ditemukan. Silakan hubungi admin.'
      loading.value = false
    }
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data ?? {}

    if (status === 422 && data?.errors) {
      errors.value = data.errors
    } else {
      generalError.value = data?.message || 'Gagal memproses pendaftaran. Silakan coba lagi.'
    }
    loading.value = false
  }
}

// ─── Step 'transfer': upload bukti ─────────────────────────────────────────────
const proofFile = ref(null)
const uploadLoading = ref(false)
const uploadError = ref('')

const uploadProof = async () => {
  if (!proofFile.value || uploadLoading.value) return

  uploadLoading.value = true
  uploadError.value = ''

  try {
    const formData = new FormData()

    formData.append('proof', proofFile.value)

    const response = await useApi(`/api/public/registrations/${transferInfo.value.registrationCode}/transfer-proof`).post({
      body: formData,
    })

    const body = response?.data ?? response
    const data = body?.data ?? body

    if (data?.whatsapp_url) {
      // Redirect penuh (beda origin) — dilewati kalau admin belum mengisi
      // nomor WA di /platform/settings, langsung ke halaman status saja.
      window.location.href = data.whatsapp_url
    } else {
      router.push({ name: 'daftar-status-registration-code', params: { registrationCode: transferInfo.value.registrationCode } })
    }
  } catch (err) {
    const data = err?.data ?? err?.response?._data ?? {}

    uploadError.value = data?.message || 'Gagal mengunggah bukti transfer. Silakan coba lagi.'
    uploadLoading.value = false
  }
}
</script>

<template>
  <div class="daftar-screen">
    <VContainer class="py-10">
      <VRow justify="center">
        <VCol
          cols="12"
          md="9"
          lg="7"
        >
          <div class="text-center mb-8">
            <RouterLink
              to="/"
              class="text-decoration-none"
            >
              <h4 class="text-h4 font-weight-bold text-primary mb-1">
                PintuKasir
              </h4>
            </RouterLink>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Daftar sekarang, pilih paket sesuai kebutuhan usaha Anda
            </p>
          </div>

          <VCard
            v-if="step === 'form'"
            rounded="xl"
            elevation="4"
          >
            <VCardText class="pa-6 pa-sm-8">
              <VAlert
                v-if="generalError"
                type="error"
                variant="tonal"
                class="mb-5"
                closable
                @click:close="generalError = ''"
              >
                {{ generalError }}
              </VAlert>

              <VForm @submit.prevent="submit">
                <!-- Data Bisnis -->
                <h6 class="text-subtitle-1 font-weight-bold mb-4">
                  Data Bisnis Anda
                </h6>
                <VRow>
                  <VCol
                    cols="12"
                    sm="6"
                  >
                    <VTextField
                      v-model="form.business_name"
                      label="Nama Bisnis"
                      placeholder="Kopi Senja"
                      variant="outlined"
                      :error-messages="errors.business_name"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="6"
                  >
                    <VTextField
                      v-model="form.owner_name"
                      label="Nama Pemilik"
                      placeholder="Budi Santoso"
                      variant="outlined"
                      :error-messages="errors.owner_name"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="6"
                  >
                    <VTextField
                      v-model="form.email"
                      type="email"
                      label="Email"
                      placeholder="budi@email.com"
                      variant="outlined"
                      :error-messages="errors.email"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="6"
                  >
                    <VTextField
                      v-model="form.phone"
                      type="tel"
                      label="No. HP / WhatsApp"
                      placeholder="08123456789"
                      variant="outlined"
                      :error-messages="errors.phone"
                    />
                  </VCol>
                </VRow>

                <VDivider class="my-2 mb-5" />

                <!-- Pilih Paket -->
                <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-4">
                  <h6 class="text-subtitle-1 font-weight-bold mb-0">
                    Pilih Paket
                  </h6>
                  <VBtnToggle
                    v-model="billingPeriod"
                    mandatory
                    density="compact"
                    color="primary"
                    rounded="lg"
                    class="billing-toggle"
                  >
                    <VBtn
                      v-for="period in BILLING_PERIODS"
                      :key="period"
                      :value="period"
                      size="small"
                    >
                      {{ BILLING_PERIOD_LABELS[period] }}
                    </VBtn>
                  </VBtnToggle>
                </div>

                <VAlert
                  v-if="plansError"
                  type="error"
                  variant="tonal"
                  class="mb-4"
                >
                  {{ plansError }}
                </VAlert>

                <VRow v-if="plansLoading">
                  <VCol
                    cols="12"
                    class="text-center py-6"
                  >
                    <VProgressCircular
                      indeterminate
                      color="primary"
                    />
                  </VCol>
                </VRow>

                <VRow v-else>
                  <VCol
                    v-for="plan in plans"
                    :key="plan.id"
                    cols="12"
                    sm="4"
                  >
                    <VCard
                      variant="outlined"
                      rounded="lg"
                      class="plan-pick-card h-100"
                      :class="{ 'plan-pick-card--active': form.plan_id === plan.id }"
                      @click="selectPlan(plan.id)"
                    >
                      <VCardText class="pa-4">
                        <div class="d-flex align-center justify-space-between mb-2">
                          <span class="font-weight-bold">{{ plan.name }}</span>
                          <VIcon
                            :icon="form.plan_id === plan.id ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'"
                            :color="form.plan_id === plan.id ? 'primary' : 'disabled'"
                          />
                        </div>
                        <p class="text-caption text-medium-emphasis mb-3">
                          {{ plan.tagline }}
                        </p>
                        <div class="text-h6 font-weight-bold text-primary">
                          {{ formatRupiah(perMonthPrice(plan, billingPeriod)) }}
                          <span class="text-caption text-medium-emphasis font-weight-regular">/bulan</span>
                        </div>
                      </VCardText>
                    </VCard>
                  </VCol>
                </VRow>
                <p
                  v-if="errors.plan_id"
                  class="text-error text-caption mt-1 mb-0"
                >
                  {{ errors.plan_id[0] }}
                </p>

                <!-- Ringkasan -->
                <VAlert
                  v-if="selectedPlan"
                  type="info"
                  variant="tonal"
                  class="mt-5 mb-2 text-body-2"
                >
                  Anda memilih paket <strong>{{ selectedPlan.name }}</strong> —
                  ditagih {{ BILLING_PERIOD_FREQUENCY[billingPeriod] }},
                  estimasi <strong>{{ formatRupiah(displayedPrice) }}/bulan</strong>.
                  Nominal & metode pembayaran final akan ditampilkan di langkah berikutnya.
                </VAlert>

                <VBtn
                  block
                  size="large"
                  type="submit"
                  class="mt-4"
                  :loading="loading"
                  :disabled="loading || plansLoading || !form.plan_id"
                >
                  Lanjutkan ke Pembayaran
                  <VIcon
                    icon="ri-arrow-right-line"
                    end
                  />
                </VBtn>

                <p class="text-caption text-medium-emphasis text-center mt-4 mb-0">
                  Dengan mendaftar, Anda akan diarahkan ke langkah pembayaran.
                  Akun toko Anda aktif setelah pembayaran diverifikasi oleh tim kami.
                </p>
              </VForm>
            </VCardText>
          </VCard>

          <!--
            Addendum 13, Modul BE: instruksi transfer bank manual (toggle
            platform_settings.registration_payment_method = 'bank_transfer'). 
          -->
          <VCard
            v-else
            rounded="xl"
            elevation="4"
          >
            <VCardText class="pa-6 pa-sm-8">
              <h6 class="text-h6 font-weight-bold mb-1">
                Instruksi Transfer
              </h6>
              <p class="text-body-2 text-medium-emphasis mb-5">
                Pendaftaran Anda tersimpan. Selesaikan dengan transfer ke rekening berikut,
                lalu unggah bukti transfernya.
              </p>

              <VAlert
                type="warning"
                variant="tonal"
                class="mb-5"
              >
                <p class="mb-2">
                  Transfer <strong>PERSIS</strong> sejumlah nominal di bawah (termasuk 3 digit
                  terakhir) — kode unik ini memudahkan tim kami mencocokkan transfer Anda dengan
                  mutasi rekening.
                </p>
                <div class="text-h5 font-weight-bold">
                  {{ formatRupiah(transferInfo.totalTransfer) }}
                </div>
                <p class="text-caption mb-0">
                  ({{ formatRupiah(transferInfo.amount) }} + kode unik {{ transferInfo.uniqueCode }})
                </p>
              </VAlert>

              <VList
                class="mb-5"
                density="compact"
              >
                <VListItem>
                  <template #prepend>
                    <VIcon
                      icon="ri-bank-line"
                      class="me-2"
                    />
                  </template>
                  <VListItemTitle class="font-weight-medium">
                    {{ transferInfo.bankName }}
                  </VListItemTitle>
                  <VListItemSubtitle>Nama Bank</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon
                      icon="ri-bank-card-line"
                      class="me-2"
                    />
                  </template>
                  <VListItemTitle class="font-weight-medium">
                    {{ transferInfo.bankAccountNumber }}
                  </VListItemTitle>
                  <VListItemSubtitle>Nomor Rekening</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon
                      icon="ri-user-line"
                      class="me-2"
                    />
                  </template>
                  <VListItemTitle class="font-weight-medium">
                    {{ transferInfo.bankAccountHolder }}
                  </VListItemTitle>
                  <VListItemSubtitle>Nama Pemilik Rekening</VListItemSubtitle>
                </VListItem>
              </VList>

              <VDivider class="mb-5" />

              <h6 class="text-subtitle-1 font-weight-bold mb-3">
                Unggah Bukti Transfer
              </h6>

              <VAlert
                v-if="uploadError"
                type="error"
                variant="tonal"
                class="mb-4"
                closable
                @click:close="uploadError = ''"
              >
                {{ uploadError }}
              </VAlert>

              <VFileInput
                v-model="proofFile"
                label="Foto/Screenshot Bukti Transfer (JPG/PNG/WEBP/PDF, maks 5MB)"
                variant="outlined"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                prepend-icon=""
                prepend-inner-icon="ri-attachment-2"
              />

              <VBtn
                block
                size="large"
                class="mt-4"
                color="success"
                :loading="uploadLoading"
                :disabled="uploadLoading || !proofFile"
                @click="uploadProof"
              >
                Saya Sudah Transfer, Kirim Bukti
                <VIcon
                  icon="ri-arrow-right-line"
                  end
                />
              </VBtn>

              <p class="text-caption text-medium-emphasis text-center mt-4 mb-0">
                Setelah bukti terunggah, Anda akan diarahkan ke WhatsApp admin untuk
                mempercepat verifikasi (opsional). Akun toko Anda aktif setelah pembayaran
                diverifikasi manual oleh tim kami.
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style scoped>
.daftar-screen {
  min-block-size: 100vh;
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.plan-pick-card {
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.plan-pick-card--active {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}

/*
 * Override template global (@core/.../_button.scss ".v-btn-toggle .v-btn")
 * yang memaksa SEMUA tombol di dalam VBtnToggle jadi kotak 36x36px !important
 * -- dirancang untuk toggle ikon-saja (mis. grid/list view switcher di POS),
 * bukan untuk tombol berlabel teks seperti "Bulanan"/"Tahunan" di sini.
 * Scoped ke halaman ini saja (.billing-toggle), TIDAK mengubah file
 * _button.scss template supaya toggle ikon lain di app tidak ikut berubah.
 */
.billing-toggle :deep(.v-btn) {
  inline-size: auto !important;
  padding-inline: 16px !important;
}
</style>